import { SearchExpand } from '@repo/shared-client/components/blocks'
import { InputFormField, NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import AdminBlocksLedger from '@web/src/features/admin/admin-blocks/admin-blocks-ledger'
import AdminTopBlocked from '@web/src/features/admin/admin-blocks/admin-top-blocked'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useCallback } from 'react'
import type {
  AdminBlockSearchScopeEnum,
  AdminBlockSortEnum,
  AdminBlockWindowEnum,
  AdminTopBlockedSortEnum,
} from 'types/graphql'
import { z } from 'zod'

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/blocks')({
  component: BlocksPage,
  validateSearch: z.object({
    blockedProfileId: z.string().optional(),
    blockerProfileId: z.string().optional(),
    minCount: z.number().optional(),
    page: z.number().optional(),
    search: z.string().optional(),
    searchScope: z.string().optional(),
    sortBy: z.string().optional(),
    tab: z.string().optional(),
    window: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        minCount: 1,
        page: 1,
        search: '',
        searchScope: 'EITHER',
        sortBy: 'LATEST',
        tab: 'ledger',
        window: 'ALL',
      }),
    ],
  },
  head: () =>
    seo({
      description: 'Admin Blocked Users Monitoring Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      title: `Admin - Blocked Users | ${env.VITE_APP_NAME}`,
    }),
})

function BlocksPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false })

  const tab = (search.tab as string) || 'ledger'
  const page = search.page ? Number.parseInt(String(search.page), 10) : 1
  const searchTerm = search.search || ''
  const minCount = search.minCount ? Number.parseInt(String(search.minCount), 10) : 1

  const rawSortBy = (search.sortBy as string) || (tab === 'ranking' ? 'MOST_BLOCKED' : 'LATEST')
  const ledgerSortBy = (rawSortBy === 'OLDEST' ? 'OLDEST' : 'LATEST') as AdminBlockSortEnum
  const rankingSortBy = (rawSortBy === 'MOST_BLOCKING' ? 'MOST_BLOCKING' : 'MOST_BLOCKED') as AdminTopBlockedSortEnum
  const searchScope = ((search.searchScope as string) || 'EITHER') as AdminBlockSearchScopeEnum
  const window = ((search.window as string) || 'ALL') as AdminBlockWindowEnum

  const formMethods = useForm({
    defaultValues: {
      minCount: String(minCount),
      search: searchTerm,
      searchScope,
      sortBy: tab === 'ranking' ? rankingSortBy : ledgerSortBy,
      window,
    },
  })

  const navigateWithSearch = useCallback(
    (nextSearch: Record<string, unknown>) => {
      navigate({
        search: {
          ...search,
          page: 1,
          ...nextSearch,
        },
      } as never)
    },
    [navigate, search]
  )

  const handleTabChange = (value: string) => {
    const nextSortBy = value === 'ranking' ? 'MOST_BLOCKED' : 'LATEST'
    formMethods.setValue('sortBy', nextSortBy)
    navigate({
      search: {
        ...search,
        blockedProfileId: value === 'ranking' ? undefined : search.blockedProfileId,
        blockerProfileId: value === 'ranking' ? undefined : search.blockerProfileId,
        page: 1,
        sortBy: nextSortBy,
        tab: value,
      },
    } as never)
  }

  const handleNameSearchChange = useCallback(
    (value: string) => {
      navigateWithSearch({ search: value.trim() || undefined })
    },
    [navigateWithSearch]
  )

  const filterResults = useCallback(() => {
    const nextSearch = String(formMethods.getValues('search') ?? '').trim()
    const nextSortBy = formMethods.getValues('sortBy')
    const nextSearchScope = formMethods.getValues('searchScope')
    const nextWindow = formMethods.getValues('window')
    const nextMinCount = Number.parseInt(String(formMethods.getValues('minCount') ?? '1'), 10)

    navigateWithSearch({
      minCount: Number.isNaN(nextMinCount) || nextMinCount <= 1 ? undefined : nextMinCount,
      search: nextSearch || undefined,
      searchScope: tab === 'ledger' ? nextSearchScope : undefined,
      sortBy: nextSortBy,
      window: tab === 'ranking' ? nextWindow : undefined,
    })
  }, [formMethods, navigateWithSearch, tab])

  const clearProfileFilters = () => {
    navigate({
      search: {
        ...search,
        blockedProfileId: undefined,
        blockerProfileId: undefined,
        page: 1,
      },
    } as never)
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <h1 className="mb-4 font-semibold text-2xl tracking-tight">{t('Blocked Users')}</h1>

      <Tabs className="w-full" onValueChange={handleTabChange} value={tab}>
        <TabsList className="mb-4 w-full justify-start" variant="tabs">
          <TabsTrigger value="ledger">{t('Block ledger')}</TabsTrigger>
          <TabsTrigger value="ranking">{t('Top blocked')}</TabsTrigger>
        </TabsList>

        <Form
          className="w-full"
          formMethods={formMethods}
          onSubmit={() => {
            filterResults()
          }}
        >
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            debounceTime={300}
            fieldProps={{ className: 'min-w-0 flex-1' }}
            inputProps={{ 'aria-label': t('Search blocked users') }}
            minChars={0}
            name="search"
            onSearchChange={handleNameSearchChange}
            placeholder={t('Search by profile name or email…')}
            toggleFiltersLabel={t('Toggle filters')}
          >
            <Card className="gap-0 py-0">
              <CardContent className="px-2 py-3">
                <div className="flex w-full flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
                  {tab === 'ledger' ? (
                    <>
                      <NativeSelectFormField
                        fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                        label={t('Search scope')}
                        name="searchScope"
                        onValueChange={() => {
                          filterResults()
                        }}
                      >
                        <option value="EITHER">{t('Either party')}</option>
                        <option value="BLOCKER">{t('Blocker only')}</option>
                        <option value="BLOCKED">{t('Blocked profile only')}</option>
                      </NativeSelectFormField>

                      <NativeSelectFormField
                        fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                        label={t('Sort By')}
                        name="sortBy"
                        onValueChange={() => {
                          filterResults()
                        }}
                      >
                        <option value="LATEST">{t('Latest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                      </NativeSelectFormField>
                    </>
                  ) : (
                    <>
                      <NativeSelectFormField
                        fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                        label={t('Rank by')}
                        name="sortBy"
                        onValueChange={() => {
                          filterResults()
                        }}
                      >
                        <option value="MOST_BLOCKED">{t('Most blocked')}</option>
                        <option value="MOST_BLOCKING">{t('Most blocking')}</option>
                      </NativeSelectFormField>

                      <NativeSelectFormField
                        fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                        label={t('Time window')}
                        name="window"
                        onValueChange={() => {
                          filterResults()
                        }}
                      >
                        <option value="ALL">{t('All time')}</option>
                        <option value="LAST_7_DAYS">{t('Last 7 days')}</option>
                        <option value="LAST_30_DAYS">{t('Last 30 days')}</option>
                        <option value="LAST_90_DAYS">{t('Last 90 days')}</option>
                      </NativeSelectFormField>

                      <InputFormField
                        fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                        inputProps={{ min: 1, type: 'number' }}
                        label={t('Minimum block count')}
                        name="minCount"
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </SearchExpand>
        </Form>

        {(search.blockerProfileId || search.blockedProfileId) && tab === 'ledger' && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={clearProfileFilters} size="sm" type="button" variant="outline">
              {t('Clear profile filter')}
            </Button>
          </div>
        )}

        <TabsContent className="mt-2 outline-none" value="ledger">
          <AdminBlocksLedger
            blockedProfileId={search.blockedProfileId as string | undefined}
            blockerProfileId={search.blockerProfileId as string | undefined}
            page={page}
            search={searchTerm}
            searchScope={searchScope}
            sortBy={ledgerSortBy}
          />
        </TabsContent>

        <TabsContent className="mt-2 outline-none" value="ranking">
          <AdminTopBlocked minCount={minCount} page={page} search={searchTerm} sortBy={rankingSortBy} window={window} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
